/* ==========================================================================
   Bundle Builder — JS
   - Reads tier config from DOM
   - Reads product list from <script type="application/json">
   - Manages modal selection state per tier
   - Adds a fixed-price checkout bundle variant via /cart/add.js
   - Stores selected products as bundle line-item metadata
   ========================================================================== */

(function () {
  'use strict';

  if (window.__bundleBuilderInit) return;
  window.__bundleBuilderInit = true;

  const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /**
   * State shared across all bundle builder sections on the page.
   * Each modal trigger writes to currentTier; modal reads from it.
   */
  const state = {
    currentTier: null, // { sectionId, tierId, blockId, name, quantity, bundlePrice, products, accent }
    selections: {}, // { tierId: { variantId: qty } }
    modal: null,
    lastFocus: null,
  };

  /* ---------------------------------------------------------------------- */
  /* Init                                                                    */
  /* ---------------------------------------------------------------------- */

  function init() {
    document.querySelectorAll('[data-bundle-trigger]').forEach((btn) => {
      if (btn.__bundleBound) return;
      btn.__bundleBound = true;
      btn.addEventListener('click', onTriggerClick);
    });

    document.querySelectorAll('[data-bundle-modal]').forEach((modal) => {
      if (modal.__bundleBound) return;
      modal.__bundleBound = true;
      bindModal(modal);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Trigger handling                                                        */
  /* ---------------------------------------------------------------------- */

  function onTriggerClick(e) {
    const btn = e.currentTarget;
    const tierEl = btn.closest('.bundle-tier');
    if (!tierEl) return;

    const sectionId = btn.closest('[data-section-id], .section--bundle-builder')
      ?.getAttribute('data-section-id') || findSectionIdFromTier(tierEl);

    if (!sectionId) {
      console.warn('[Bundle] section id not resolved');
      return;
    }

    const productsScript = document.querySelector(`[data-bundle-products-${sectionId}]`);
    const configScript = document.querySelector(`[data-bundle-config-${sectionId}]`);
    if (!productsScript || !configScript) {
      console.warn('[Bundle] product/config data missing');
      return;
    }

    let products = [];
    let config = {};
    try {
      products = JSON.parse(productsScript.textContent.trim() || '[]');
      config = JSON.parse(configScript.textContent.trim() || '{}');
    } catch (err) {
      console.error('[Bundle] failed to parse data', err);
      return;
    }

    const accent = getComputedStyle(tierEl).getPropertyValue('--tier-accent-color').trim() || '#1f6b3a';

    state.currentTier = {
      sectionId,
      tierId: tierEl.getAttribute('data-tier-id'),
      blockId: tierEl.getAttribute('data-tier-block-id'),
      name: tierEl.getAttribute('data-tier-name') || '',
      quantity: parseInt(tierEl.getAttribute('data-tier-quantity'), 10) || 1,
      bundlePriceRaw: tierEl.getAttribute('data-tier-bundle-price-raw') || '',
      bundlePrice: parseInt(tierEl.getAttribute('data-tier-bundle-price'), 10) || parseMoneyToCents(tierEl.getAttribute('data-tier-bundle-price-raw')) || 0,
      bundlePriceFormatted: parseInt(tierEl.getAttribute('data-tier-bundle-price'), 10)
        ? tierEl.getAttribute('data-tier-bundle-price-formatted') || ''
        : '',
      checkoutVariantId: parseInt(tierEl.getAttribute('data-tier-checkout-variant-id'), 10) || 0,
      checkoutVariantTitle: tierEl.getAttribute('data-tier-checkout-variant-title') || '',
      image: tierEl.getAttribute('data-tier-image') || '',
      imageAlt: tierEl.getAttribute('data-tier-image-alt') || '',
      products,
      config,
      accent,
    };

    if (!state.selections[state.currentTier.tierId]) {
      state.selections[state.currentTier.tierId] = {};
    }

    const modal = document.getElementById(`bundle-modal-${sectionId}`);
    if (!modal) return;
    state.modal = modal;
    openModal(modal);
  }

  function findSectionIdFromTier(tierEl) {
    // Tier id pattern: `${sectionId}-${blockId}`
    const id = tierEl.getAttribute('data-tier-id') || '';
    const blockId = tierEl.getAttribute('data-tier-block-id') || '';
    if (!blockId || !id.endsWith('-' + blockId)) return null;
    return id.slice(0, id.length - blockId.length - 1);
  }

  /* ---------------------------------------------------------------------- */
  /* Modal                                                                   */
  /* ---------------------------------------------------------------------- */

  function bindModal(modal) {
    modal.querySelectorAll('[data-bundle-close]').forEach((el) => {
      el.addEventListener('click', () => closeModal(modal));
    });

    const addBtn = modal.querySelector('[data-bundle-add-to-cart]');
    if (addBtn) addBtn.addEventListener('click', () => onAddToCart(modal));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.getAttribute('data-open') === 'true') {
        closeModal(modal);
      }
      if (e.key === 'Tab' && modal.getAttribute('data-open') === 'true') {
        trapFocus(e, modal);
      }
    });
  }

  function openModal(modal) {
    state.lastFocus = document.activeElement;
    modal.setAttribute('data-open', 'true');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Apply accent color to modal
    if (state.currentTier?.accent) {
      modal.style.setProperty('--tier-accent-color', state.currentTier.accent);
    }

    renderProducts(modal);
    updateFooter(modal);

    // Focus first focusable
    requestAnimationFrame(() => {
      const first = modal.querySelector(FOCUSABLE);
      if (first) first.focus();
    });
  }

  function closeModal(modal) {
    modal.setAttribute('data-open', 'false');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      state.lastFocus.focus();
    }
  }

  function trapFocus(e, modal) {
    const focusables = Array.from(modal.querySelectorAll(FOCUSABLE)).filter(
      (el) => !el.disabled && el.offsetParent !== null
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Product rendering                                                       */
  /* ---------------------------------------------------------------------- */

  function renderProducts(modal) {
    const list = modal.querySelector('[data-bundle-product-list]');
    if (!list) return;
    list.innerHTML = '';

    const tier = state.currentTier;
    if (!tier || !tier.products.length) {
      list.innerHTML = `<div class="bundle-modal__empty">No products available. Please configure a collection in the theme editor.</div>`;
      return;
    }

    tier.products.forEach((product) => {
      list.appendChild(renderProductCard(product, tier));
    });
  }

  function renderProductCard(product, tier) {
    const card = document.createElement('div');
    card.className = 'bundle-product';
    card.setAttribute('data-product-id', product.id);

    const unavailable = !product.available;
    if (unavailable) card.setAttribute('data-unavailable', 'true');

    const initialVariant = product.variants.find((v) => v.available) || product.variants[0];
    card.setAttribute('data-variant-id', initialVariant ? initialVariant.id : '');

    // Image
    const imgWrap = document.createElement('div');
    imgWrap.className = 'bundle-product__image';
    if (product.featured_image) {
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.src = product.featured_image;
      img.alt = product.title;
      imgWrap.appendChild(img);
    }
    card.appendChild(imgWrap);

    // Title
    const title = document.createElement('h4');
    title.className = 'bundle-product__title';
    title.textContent = product.title;
    card.appendChild(title);

    // Price
    const price = document.createElement('div');
    price.className = 'bundle-product__price';
    price.textContent = (initialVariant && initialVariant.price_formatted) || product.price_formatted;
    card.appendChild(price);

    // Variant selector
    if (!product.has_only_default_variant && product.variants.length > 1) {
      const select = document.createElement('select');
      select.className = 'bundle-product__variant-select';
      select.setAttribute('aria-label', 'Select variant');
      product.variants.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.available ? v.title : `${v.title} — ${tier.config.outOfStockText}`;
        opt.disabled = !v.available;
        select.appendChild(opt);
      });
      select.value = String(initialVariant ? initialVariant.id : '');
      select.addEventListener('click', (e) => e.stopPropagation());
      select.addEventListener('change', (e) => {
        e.stopPropagation();
        const newVariantId = e.target.value;
        const oldVariantId = card.getAttribute('data-variant-id');

        // Migrate existing selections from old variant to new variant
        const sel = state.selections[tier.tierId];
        if (sel[oldVariantId]) {
          sel[newVariantId] = (sel[newVariantId] || 0) + sel[oldVariantId];
          delete sel[oldVariantId];
        }
        card.setAttribute('data-variant-id', newVariantId);

        // Update price
        const v = product.variants.find((vv) => String(vv.id) === String(newVariantId));
        if (v) price.textContent = v.price_formatted;

        updateCard(card, tier);
        updateFooter(state.modal);
      });
      card.appendChild(select);
    }

    if (unavailable) {
      const oos = document.createElement('div');
      oos.className = 'bundle-product__unavailable';
      oos.textContent = tier.config.outOfStockText || 'Out of stock';
      card.appendChild(oos);
      return card;
    }

    // Quantity controls
    const qtyWrap = document.createElement('div');
    qtyWrap.className = 'bundle-product__qty';

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'bundle-product__qty-btn';
    minus.setAttribute('aria-label', 'Decrease quantity');
    minus.textContent = '−';
    minus.addEventListener('click', (e) => {
      e.stopPropagation();
      changeQty(card, tier, -1);
    });

    const value = document.createElement('span');
    value.className = 'bundle-product__qty-value';
    value.setAttribute('data-qty-value', '');
    value.textContent = '0';

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'bundle-product__qty-btn';
    plus.setAttribute('aria-label', 'Increase quantity');
    plus.textContent = '+';
    plus.addEventListener('click', (e) => {
      e.stopPropagation();
      changeQty(card, tier, 1);
    });

    qtyWrap.appendChild(minus);
    qtyWrap.appendChild(value);
    qtyWrap.appendChild(plus);
    card.appendChild(qtyWrap);

    // Card click → +1 (when not clicking the controls themselves)
    card.addEventListener('click', () => changeQty(card, tier, 1));

    updateCard(card, tier);
    return card;
  }

  function changeQty(card, tier, delta) {
    const variantId = card.getAttribute('data-variant-id');
    if (!variantId) return;

    const sel = state.selections[tier.tierId];
    const current = sel[variantId] || 0;
    const totalSelected = totalSelectedFor(tier.tierId);

    let next = current + delta;
    if (next < 0) next = 0;

    // Cap at tier quantity
    if (delta > 0 && totalSelected >= tier.quantity) {
      flashError(state.modal, `You can only select ${tier.quantity} item${tier.quantity > 1 ? 's' : ''} for this bundle.`);
      return;
    }

    if (next === 0) delete sel[variantId];
    else sel[variantId] = next;

    updateAllCards(state.modal, tier);
    updateFooter(state.modal);
  }

  function updateCard(card, tier) {
    const variantId = card.getAttribute('data-variant-id');
    const sel = state.selections[tier.tierId];
    const count = (variantId && sel[variantId]) || 0;

    card.setAttribute('data-selected-count', String(count));

    const valueEl = card.querySelector('[data-qty-value]');
    if (valueEl) valueEl.textContent = String(count);

    // Badge
    let badge = card.querySelector('.bundle-product__badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'bundle-product__badge';
        card.appendChild(badge);
      }
      badge.textContent = String(count);
    } else if (badge) {
      badge.remove();
    }

    // Disable +/- when appropriate
    const total = totalSelectedFor(tier.tierId);
    const remaining = tier.quantity - total;
    const minusBtn = card.querySelectorAll('.bundle-product__qty-btn')[0];
    const plusBtn = card.querySelectorAll('.bundle-product__qty-btn')[1];
    if (minusBtn) minusBtn.disabled = count === 0;
    if (plusBtn) plusBtn.disabled = remaining <= 0;
  }

  function updateAllCards(modal, tier) {
    modal.querySelectorAll('.bundle-product').forEach((c) => updateCard(c, tier));
  }

  function totalSelectedFor(tierId) {
    const sel = state.selections[tierId] || {};
    return Object.values(sel).reduce((s, n) => s + n, 0);
  }

  /* ---------------------------------------------------------------------- */
  /* Footer / progress                                                       */
  /* ---------------------------------------------------------------------- */

  function updateFooter(modal) {
    if (!modal || !state.currentTier) return;
    const tier = state.currentTier;
    const total = totalSelectedFor(tier.tierId);
    const required = tier.quantity;
    const pct = Math.min(100, (total / required) * 100);

    const countEl = modal.querySelector('[data-bundle-count]');
    if (countEl) countEl.textContent = `${total} / ${required} ${tier.config.selectedSuffix || 'selected'}`;

    const pricingEl = modal.querySelector('[data-bundle-pricing]');
    if (pricingEl) {
      const originalTotal = selectedOriginalTotal(tier);
      const bundleTotal = tier.bundlePrice || originalTotal;
      const bundleTotalText = tier.config.bundleTotalText || 'Bundle total';
      const originalTotalText = tier.config.originalTotalText || 'Regular total';
      pricingEl.textContent = `${bundleTotalText}: ${formatMoney(bundleTotal, tier)}${
        originalTotal > bundleTotal ? ` (${originalTotalText}: ${formatMoney(originalTotal, tier)})` : ''
      }`;
    }

    const bar = modal.querySelector('[data-bundle-progress-bar]');
    if (bar) bar.style.width = pct + '%';

    const progressText = modal.querySelector('[data-bundle-progress-text]');
    if (progressText) {
      const remaining = required - total;
      if (remaining > 0) {
        progressText.textContent = `${tier.name ? tier.name + ' — ' : ''}select ${remaining} more`;
      } else {
        progressText.textContent = `${tier.name ? tier.name + ' — ' : ''}ready to add`;
      }
    }

    const addBtn = modal.querySelector('[data-bundle-add-to-cart]');
    if (addBtn) addBtn.disabled = total !== required;
  }

  function selectedOriginalTotal(tier) {
    const sel = state.selections[tier.tierId] || {};
    return Object.entries(sel).reduce((sum, [variantId, qty]) => {
      const variant = findVariant(tier, variantId);
      return sum + ((variant && Number(variant.price)) || 0) * qty;
    }, 0);
  }

  function findVariant(tier, variantId) {
    const target = String(variantId);
    for (const product of tier.products || []) {
      const variant = (product.variants || []).find((v) => String(v.id) === target);
      if (variant) return variant;
    }
    return null;
  }

  function findProductForVariant(tier, variantId) {
    const target = String(variantId);
    return (tier.products || []).find((product) =>
      (product.variants || []).some((variant) => String(variant.id) === target)
    ) || null;
  }

  function formatMoney(cents, tier) {
    if (tier.bundlePrice && cents === tier.bundlePrice && tier.bundlePriceFormatted) {
      return tier.bundlePriceFormatted;
    }

    const currency = tier.config.currencyCode || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format((Number(cents) || 0) / 100);
    } catch (err) {
      return ((Number(cents) || 0) / 100).toFixed(2);
    }
  }

  function parseMoneyToCents(value) {
    if (value == null) return 0;

    const numeric = String(value).replace(/[^0-9.]/g, '');
    if (!numeric) return 0;

    return Math.round(parseFloat(numeric) * 100) || 0;
  }

  function flashError(modal, msg) {
    if (!modal) return;
    const errEl = modal.querySelector('[data-bundle-error]');
    if (!errEl) return;
    errEl.textContent = msg;
    errEl.hidden = false;
    clearTimeout(errEl.__t);
    errEl.__t = setTimeout(() => {
      errEl.hidden = true;
      errEl.textContent = '';
    }, 2500);
  }

  /* ---------------------------------------------------------------------- */
  /* Add to cart                                                             */
  /* ---------------------------------------------------------------------- */

  async function onAddToCart(modal) {
    const tier = state.currentTier;
    if (!tier) return;
    const sel = state.selections[tier.tierId] || {};
    const total = totalSelectedFor(tier.tierId);
    if (total !== tier.quantity) return;

    if (tier.bundlePrice && !tier.checkoutVariantId) {
      const message = `Bundle checkout variant is missing. Enter the checkout product handle and make sure it has a "${tier.name || tier.quantity + ' pack'}" variant.`;
      flashError(modal, message);
      showToast(message, true);
      return;
    }

    const addBtn = modal.querySelector('[data-bundle-add-to-cart]');
    const addText = modal.querySelector('[data-bundle-add-text]');
    const addLoading = modal.querySelector('[data-bundle-add-loading]');
    addBtn.disabled = true;
    if (addText) addText.hidden = true;
    if (addLoading) addLoading.hidden = false;

    const bundleId = generateBundleId();
    const originalTotal = selectedOriginalTotal(tier);
    const bundlePrice = tier.bundlePrice || originalTotal;
    const manualDiscount = Math.max(0, originalTotal - bundlePrice);
    const bundleProperties = buildBundleProperties(tier, sel, {
      bundleId,
      bundlePrice,
      originalTotal,
      manualDiscount,
    });
    const items = tier.checkoutVariantId
      ? [{
          id: tier.checkoutVariantId,
          quantity: 1,
          properties: bundleProperties,
        }]
      : Object.entries(sel).map(([variantId, qty]) => ({
          id: parseInt(variantId, 10),
          quantity: qty,
          properties: bundleProperties,
        }));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.description || errData.message || 'Add to cart failed');
      }

      // Notify theme of cart update
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      // Either redirect to cart or close the modal after adding the bundle.
      if (tier.config.cartRedirect) {
        window.location.href = '/cart';
        return;
      }

      showToast(tier.config.successMessage || 'Bundle added to cart!', false);
      // Reset state for this tier
      state.selections[tier.tierId] = {};
      closeModal(modal);
    } catch (err) {
      console.error('[Bundle] add-to-cart error', err);
      showToast(tier.config.errorMessage || 'Could not add bundle.', true);
      flashError(modal, err.message || 'Could not add bundle.');
    } finally {
      addBtn.disabled = false;
      if (addText) addText.hidden = false;
      if (addLoading) addLoading.hidden = true;
    }
  }

  function generateBundleId() {
    return 'bundle_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  function buildBundleProperties(tier, selections, totals) {
    const contents = selectedBundleContents(tier, selections);
    const properties = {
      _bundle_id: totals.bundleId,
      _bundle_name: tier.name,
      _bundle_size: String(tier.quantity),
      _bundle_checkout_mode: tier.checkoutVariantId ? 'single_variant' : 'separate_items',
      _bundle_fixed_price: formatMoney(totals.bundlePrice, tier),
      _bundle_fixed_price_cents: String(totals.bundlePrice),
      _bundle_original_total: formatMoney(totals.originalTotal, tier),
      _bundle_original_total_cents: String(totals.originalTotal),
      _bundle_manual_discount: formatMoney(totals.manualDiscount, tier),
      _bundle_manual_discount_cents: String(totals.manualDiscount),
      _bundle_contents: contents,
      ...(tier.image ? { _bundle_image: tier.image } : {}),
      ...(tier.imageAlt ? { _bundle_image_alt: tier.imageAlt } : {}),
    };

    if (tier.checkoutVariantId) {
      properties['Selected products'] = contents;
    }

    return properties;
  }

  function selectedBundleContents(tier, selections) {
    return Object.entries(selections).map(([variantId, qty]) => {
      const product = findProductForVariant(tier, variantId);
      const variant = findVariant(tier, variantId);
      const variantTitle = variant && variant.title && variant.title !== 'Default Title'
        ? ` - ${variant.title}`
        : '';
      const title = product ? product.title : `Variant ${variantId}`;
      return `${title}${variantTitle} x${qty}`;
    }).join(', ');
  }

  /* ---------------------------------------------------------------------- */
  /* Toast                                                                   */
  /* ---------------------------------------------------------------------- */

  function showToast(msg, isError) {
    const t = document.createElement('div');
    t.className = 'bundle-toast' + (isError ? ' bundle-toast--error' : '');
    t.textContent = msg;
    t.setAttribute('role', 'status');
    document.body.appendChild(t);
    requestAnimationFrame(() => t.classList.add('bundle-toast--visible'));
    setTimeout(() => {
      t.classList.remove('bundle-toast--visible');
      setTimeout(() => t.remove(), 350);
    }, 2500);
  }

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                    */
  /* ---------------------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor section load/select
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
